import { ConfigService } from "./../../../services/config.service";
import { Component, ElementRef, HostListener, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  DxButtonModule,
  DxDataGridModule,
  DxDateBoxComponent,
  DxDateBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
  DxToastModule,
  DxValidatorModule,
  DxValidationGroupModule,
  DxValidationGroupComponent,
} from "devextreme-angular";
import { ContactService } from "src/app/services/contact.service";
import { GridConnectionService } from "src/app/services/grid-connection.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AppGlobals } from "src/app/app.globals";
import { DeletePopupComponent } from "src/app/components/delete-popup/delete-popup.component";
import { environment } from "src/environments/environment";
import { FeatureFlags } from "src/app/interfaces/enums/feature-flag";
import { Observable, ObservableInput, of, Subject, takeUntil } from "rxjs";
import { HasChanges } from "src/app/services/guards/unsaved-changes.guard";
import { ContractService } from "src/app/services/contracts/contract.service";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { GpdPillComponent } from "src/app/components/gpd-pill/gpd-pill.component";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { FormFooterComponent } from "src/app/components/form-footer/form-footer.component";
import { ContractsPillComponent } from "src/app/components/contracts-pill/contracts-pill.component";
import { GetContactProjectsResultModel } from "src/app/interfaces/entities/contact";
import { custom } from "devextreme/ui/dialog";
import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivityEntity, ActivitySection } from "src/app/constants/activity-log";
import { ContactAddProjectModalComponent } from "src/app/pages/contacts/contact-add-project-modal/contact-add-project-modal.component";
import { ContactPillComponent, ContactPillValue } from "src/app/components/contact-pill/contact-pill.component";
import { ValidationToastComponent } from "src/app/components/validation-toast/validation-toast.component";

import { SearchableDropdownDirective } from '../../../directives/searchable-dropdown.directive';
@Component({
    selector: "app-contacts-person-details",
    imports: [
        CommonModule,
        DxPopupModule,
        DxButtonModule,
        DxDataGridModule,
        DeletePopupComponent,
        DxToastModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxSelectBoxModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        GpdButtonComponent,
        GpdPillComponent,
        SvgIconComponent,
        FormFooterComponent,
        ContractsPillComponent,
        ContactAddProjectModalComponent,
        ValidationToastComponent,
        SearchableDropdownDirective
    ],
    templateUrl: "./contacts-person-details.page.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./contacts-person-details.page.css"]
})
export class ContactsPersonDetailsComponent implements HasChanges {
  @ViewChild("personGrid") personGrid;
  @ViewChild("personValidationGroup") personValidationGroup: DxValidationGroupComponent;
  @ViewChild("validationToast") validationToast: ValidationToastComponent;
  @ViewChild("projectsListEl") projectsListEl?: ElementRef<HTMLElement>;
  @ViewChild("contractsListEl") contractsListEl?: ElementRef<HTMLElement>;

  // Search box only makes sense once the list actually needs scrolling - measured once
  // against the full (unfiltered) list right after it loads, not re-measured while typing,
  // so the box doesn't vanish mid-search as filtering shrinks the visible rows.
  projectsListScrollable = false;
  contractsListScrollable = false;

  private updateProjectsScrollable(): void {
    setTimeout(() => {
      const el = this.projectsListEl?.nativeElement;
      this.projectsListScrollable = !!el && el.scrollHeight > el.clientHeight;
    });
  }

  private updateContractsScrollable(): void {
    setTimeout(() => {
      const el = this.contractsListEl?.nativeElement;
      this.contractsListScrollable = !!el && el.scrollHeight > el.clientHeight;
    });
  }

  currentPersonId: any;
  person: any;
  contactTags: any;
  personCompanies: (ContactPillValue & { role: string })[] = [];

  addProjectModalVisible: boolean = false;

  editMode: boolean = true;

  toastVisible: boolean = false;
  type = "success";
  message: any = $localize`:@@changesSaved:Changes Saved`;

  personCopy: any;

  // Same unsaved-changes guard as the contract form (see hasChangesGuard): baseline
  // snapshot taken once the person has loaded, compared against on every navigation
  // attempt away from this page.
  hasChanges: boolean = false;
  isSavingChanges: boolean = false;
  private initialPerson: any;

  deletePopupVisible: boolean = false;

  salutations = [{ name: "Herr" }, { name: "Frau" }];

  countriesByLanguageList: any = [];
  statesByLanguageList: any = [];
  relatedProjects: any;
  private projectsStatusById: Map<number, string> = new Map();
  private projectsFromDetails: Array<{ id: number; nr: string; name: string; status: string | null }> = [];
  isCtrlPressed: boolean;
  affectedProjectsList: any = [];
  showProjectsList: boolean = false;
  isGridVisible: boolean = false;
  confirmationMessage: string = "";
  popupWidth: number = null;
  popupHeight: number = null;
  contractsEnabled: boolean =  false;
  relatedContracts: any = [];

  $unsubscribe = new Subject<any>;
  featureFlagsLoaded: boolean = false

  private syncRelatedProjectStatuses(): void {
    if (!Array.isArray(this.relatedProjects)) return;
    this.relatedProjects.forEach((item: any) => {
      const project = item?.project;
      if (!project) return;
      const projectId = Number(project.projectId ?? project.id ?? item.projectId ?? item.id);
      if (!project.status && projectId) {
        project.status = this.projectsStatusById.get(projectId) ?? null;
      }
      if (!project.status) {
        const nr = project.nr ?? item.nr ?? "";
        const name = project.projectName ?? project.name ?? item.projectName ?? item.displayName ?? item.name ?? "";
        const matched = this.projectsFromDetails.find((p) => p.nr === nr && p.name === name);
        project.status = matched?.status ?? null;
      }
    });
  }

  constructor(
    private contactService: ContactService,
    private contractService: ContractService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private gridConnectionService: GridConnectionService,
    private appGlobals: AppGlobals,
    private activityLog: ActivityLogService
  ) { }

  ngOnInit(): void {
    this.currentPersonId = this.activatedRoute.snapshot.url[1].path;

    this.appGlobals.$features
    .pipe(takeUntil(this.$unsubscribe))
    .subscribe(() => {
      this.contractsEnabled = AppGlobals.UserCan(
        FeatureFlags.Contracts
      );

      this.featureFlagsLoaded = true;
      //wait for feature flags to be loaded 
      this.getPageData();
      this.getCountriesByLanguage();
    });

    
    /* this.getStatesByLanguage(2); */
  }

  getPageData() {
    this.contactService
      .getPersonDetails({ PersonId: this.currentPersonId })
      .then(
        (result) => {
          if (result.hasError === true)
            AppGlobals.ShowErrorMessage(result.feedback);
          else {
            this.person = result.personDetailItem.personDetails;
            this.activityLog.logView({
              Section: ActivitySection.CONTACTS_PERSON,
              EntityType: ActivityEntity.PERSON,
              EntityId: Number(this.currentPersonId),
              EntityLabel: `${this.person?.firstname ?? ''} ${this.person?.lastname ?? ''}`.trim(),
            });
            this.personCopy = result.personDetailItem.personDetails;
            this.initialPerson = JSON.parse(JSON.stringify(this.person));
            // `type` is what app-contact-pill routes on, so the row itself doubles as the pill value.
            // The ?? [] matters: an API build without `companies` would otherwise throw here and
            // abort the related-projects setup below it.
            this.personCompanies = (result.personDetailItem.companies ?? []).map((c: any) => ({
              ...c,
              type: "company" as const,
            }));
            const projects = result.personDetailItem.projects ?? [];
            this.projectsStatusById = new Map(
              projects.map((p: any) => [Number(p.id), p.status])
            );
            this.projectsFromDetails = projects.map((p: any) => ({
              id: Number(p.id),
              nr: p.nr ?? "",
              name: p.name ?? "",
              status: p.status ?? null,
            }));
            this.syncRelatedProjectStatuses();
          }
        },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        }
      );

    this.configService.getContactTags({}).then(
      (result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.contactTags = result.contactTags;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );

    this.loadRelatedProjects();

    let payload = {
      contactId: this.currentPersonId,
      contactType: "person",
    };

      if(this.contractsEnabled) {
        this.contactService.getRelatedContracts({ ContactProjectRequestItem: payload }).then(result => {
          if (result.hasError === true) {
            AppGlobals.ShowErrorMessage(result.feedback);
          } else {
            this.relatedContracts = result.contracts;
            this.updateContractsScrollable();
          }
        });
      }
  }

  // Also called after "Add to Project" links a new project, so the grid reflects it
  // without a full page reload.
  private loadRelatedProjects(): void {
    const payload = {
      contactId: this.currentPersonId,
      contactType: "person",
    };

    this.contactService
      .getContactProjects({ ContactProjectRequestItem: payload })
      .then((result: GetContactProjectsResultModel) => {
        result.contactProjectItems.forEach((item) => {
          const project = item.project ?? {};
          project.projectId = project.projectId ?? project.id ?? item.projectId ?? item.id ?? null;
          project.siteId = project.siteId ?? item.siteId ?? null;
          project.nr = project.nr ?? item.nr ?? "";
          project.projectName =
            project.projectName ??
            project.displayName ??
            project.name ??
            project.title ??
            item.projectName ??
            item.displayName ??
            item.name ??
            (project.projectId ? `${project.projectId}` : "");
          project.status =
            item.status ??
            project.status ??
            project.projectStatus ??
            this.projectsStatusById.get(Number(project.projectId)) ??
            null;
          item.project = project;

          item.routingInfo?.forEach((route) => {
            if (route.description === 'Cost Partner') {
              route.description = $localize`:@@costPartner:Service Provider`;
            }
            if (route.description === 'Cost Debitor') {
              route.description = $localize`:@@costDebitor:Debtor`;
            }
          });
        });
        this.relatedProjects = result.contactProjectItems;
        this.syncRelatedProjectStatuses();
        this.updateProjectsScrollable();
      });
  }

  onProjectAdded(): void {
    this.loadRelatedProjects();
  }

  relatedProjectsSearchTerm: string = '';

  get filteredRelatedProjects(): any[] {
    if (!this.relatedProjects) return [];
    const term = (this.relatedProjectsSearchTerm || '').trim().toLowerCase();
    if (!term) return this.relatedProjects;
    return this.relatedProjects.filter((item: any) =>
      (item.project?.projectName || '').toLowerCase().includes(term)
    );
  }

  // Derived source routing (task, cost, property, component) to show as pills.
  // Excludes the internal "manual" marker — the manual link is represented by its own
  // removable role pill instead (data.role / removeManualProjectRole).
  sourceRouting(data: any): any[] {
    return (data?.routingInfo ?? []).filter((r: any) => r.descriptionRouteType !== 'manual');
  }

  // Staged role pending confirmation in the delete-role popup below.
  deleteRoleVisible = false;
  deleteRoleMessage = '';
  private roleToRemove: any = null;

  removeManualProjectRole(item: any): void {
    if (!item?.projectContactId) {
      return;
    }
    this.roleToRemove = item;
    this.deleteRoleMessage = $localize`:@@deleteRoleConfirm:Are you sure you want to remove the role ${
      '<b>' + (item.role ?? '') + '</b>'
    }:role: from ${'<b>' + (item.project?.projectName ?? '') + '</b>'}:project:?`;
    this.deleteRoleVisible = true;
  }

  onDeleteRoleConfirmed(): void {
    const item = this.roleToRemove;
    this.deleteRoleVisible = false;
    this.roleToRemove = null;
    if (!item?.projectContactId) {
      return;
    }
    this.contactService
      .removeProjectContact({ ProjectContactId: item.projectContactId })
      .then((result: any) => {
        if (result?.hasError === true) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.loadRelatedProjects();
        }
      })
      .catch((error: any) => AppGlobals.ShowErrorMessage(error));
  }

  onDeleteRoleAborted(): void {
    this.deleteRoleVisible = false;
    this.roleToRemove = null;
  }

  hasUnsavedChanges(): boolean {
    return this.editMode || this.person != this.personCopy;
  }

  goTo(link) {
    this.router.navigate([link]);
  }

  gotoCompany(company: any): void {
    this.router.navigate(["/contacts/company/" + company.id]);
  }

  getTag(item) {
    return this.contactTags.find((tag) => tag.id == item).title;
  }

  // The "x" on a pill removes just that tag — the dropdown below stays in sync since
  // both render from the same person.tags array.
  removeTag(item: any): void {
    this.person.tags = (this.person.tags ?? []).filter((t: any) => t != item);
  }

  setEditMode(arg) {
    if (arg === true) {
      this.personCopy = structuredClone(this.person);
    }

    if (arg === false) {
      this.person = this.personCopy;
    }

    this.editMode = arg;
  }

  save(e) {
    const validation = this.personValidationGroup.instance.validate();
    if (!validation.isValid) {
      this.validationToast.showToast($localize`Please fill in all fields before saving`);
      return;
    }

    this.hasChanges = false;
    this.isSavingChanges = true;

    this.contactService
      .writePersonDetails({ PersonItemModel: this.person })
      .then(
        (result) => {
          if (result.hasError === true) {
            this.isSavingChanges = false;
            AppGlobals.ShowErrorMessage(result.feedback);
          } else {
            this.router.navigate(['contacts/persons']);
          }
        },
        (error: any) => {
          this.isSavingChanges = false;
          AppGlobals.ShowErrorMessage(error);
        }
      );
  }

  checkHasChanges(): void {
    if (!this.initialPerson) {
      this.hasChanges = false;
      return;
    }
    this.hasChanges = JSON.stringify(this.person) !== JSON.stringify(this.initialPerson);
  }

  saveUnsavedChanges(): Observable<boolean> {
    this.save(null);
    return of(false);
  }

  deletePerson() {
    this.contactService.deletePerson({ PersonId: this.currentPersonId }).then(
      (result) => {
        if (result.isInUse === true) {
          custom({
            title: $localize`:@@deletionNotPossibleTitle:Deletion not possible`,
            messageHtml: $localize`:@@contactInUseErrorMessage:This contact is still linked to projects or contracts. Please remove these links first.`,
            buttons: [{ text: $localize`:@@confirm:Confirm`, onClick: () => true }],
          }).show();
        } else if (result.hasError === true) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.router.navigate(["contacts/persons"]);
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  confirmDelete(): void {
    this.showProjectsList = true;
    this.contactService
      .ConfirmDeletePerson({ PersonId: this.currentPersonId })
      .then((result) => {
        const projects =
          result.affectedProjects?.map((project: any) => project.pva) || [];
        // Adjust popup size dynamically
        this.popupWidth = this.isGridVisible ? 900 : 500;
        this.popupHeight = this.isGridVisible ? 600 : 250;
        // Determine visibility and message based on project presence
        this.isGridVisible = projects.length > 0;
        this.confirmationMessage = this.isGridVisible
          ? "The user is associated with these projects. Do you still want to delete?"
          : "Do you want to delete?";

        // Format the projects for a two-column display
        this.affectedProjectsList = [];
        for (let i = 0; i < projects.length; i += 2) {
          this.affectedProjectsList.push({
            projectName1: projects[i],
            projectName2: projects[i + 1] || "", // Handle odd number of projects
          });
        }
      });
  }

  getCountriesByLanguage(): void {
    this.gridConnectionService.getCountries({}).then(
      (result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.countriesByLanguageList = result.countryList;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  /*   getStatesByLanguage(countryID: any): void {
      let selectedCountry = { "CountryId": countryID, }
  
      this.gridConnectionService.getStates(selectedCountry).then((result) => {
        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.statesByLanguageList = result.stateList;
        }
      },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        });
  
    } */

  handleCountryChange(e: any): void {
    /*     this.getStatesByLanguage(e); */
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(event: KeyboardEvent) {
    if (event.key === "Control" || event.key === "Meta") {
      this.isCtrlPressed = true;
    }
  }

  @HostListener("document:keyup", ["$event"])
  onKeyup(event: KeyboardEvent) {
    if (event.key === "Control" || event.key === "Meta") {
      this.isCtrlPressed = false;
    }
  }

  onCellClick(e: any) {
    if (e.columnIndex == 0) {
      if (e.data.projectType == "project") {
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${e.data.siteId}/project/${e.data.projectId}`,
          ]);
        } else {
          window.open(`site/${e.data.siteId}/project/${e.data.projectId}`);
        }
      }

      if (e.data.projectType == "grid") {
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${e.data.siteId}`]);
        } else {
          window.open(`site/${e.data.siteId}`);
        }
      }

      if (e.data.projectType == "grid") {
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${e.data.siteId}`]);
        } else {
          window.open(`site/${e.data.siteId}`);
        }
      }
    }
  }

  onItemClick(data, routeData: any) {
    console.log(data);
    switch (routeData.descriptionRouteType) {
      case "task":
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${data.siteId}/project/${data.projectId}/tasks/${routeData.descriptionRouteId}`,
          ]);
        } else {
          window.open(
            `site/${data.siteId}/project/${data.projectId}/tasks/${routeData.descriptionRouteId}`
          );
        }
        break;

      case "property":
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${routeData.descriptionRouteId}/properties/`,
          ]);
        } else {
          window.open(`site/${routeData.descriptionRouteId}/properties/`);
        }
        break;

      case "gridComponent":
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${data.siteId}/gridConnectionComponents`,
          ]);
        } else {
          window.open(`site/${data.siteId}/gridConnectionComponents`);
        }
        break;

      case "projectComponent":
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${data.siteId}/project/${data.projectId}/projectInfo/components`,
          ]);
        } else {
          window.open(
            `site/${data.siteId}/project/${data.projectId}/projectInfo/components`
          );
        }
        break;

      case "project-cost":
        if (!this.isCtrlPressed) {
          this.router.navigate([
            `site/${data.siteId}/project/${data.projectId}/project-costs`,
          ]);
        } else {
          window.open(
            `site/${data.siteId}/project/${data.projectId}/project-costs`
          );
        }
        break;
    }
  }

  relatedContractsSearchTerm: string = '';

  get filteredRelatedContracts(): any[] {
    if (!this.relatedContracts) return [];
    const term = (this.relatedContractsSearchTerm || '').trim().toLowerCase();
    if (!term) return this.relatedContracts;
    return this.relatedContracts.filter((item: any) =>
      (item.designation || '').toLowerCase().includes(term)
    );
  }

  onRelatedContractsRowClick($event: any) {
    this.contractService.setPreviousUrl(this.router.url);
    this.router.navigate([
      `contracts/edit/${$event.data.id}`,
    ]);
  }
}
